import axios, { AxiosInstance } from 'axios';
import { getApiBaseUrl, buildApiUrl } from './apiConfig';

// API base configuration - uses centralized config for environment-aware URL resolution
const API_BASE_URL = getApiBaseUrl();

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load token from localStorage
    this.token = localStorage.getItem('authToken');

    // Request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Don't redirect on login/auth endpoints — let the Login component handle those errors
          const url = error.config?.url || '';
          if (!url.includes('/auth/login') && !url.includes('/auth/change-password')) {
            this.logout();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login', { email, password });

    // If user must change password on first login, don't store token yet
    if (response.data.mustChangePassword) {
      return response.data;
    }

    this.token = response.data.token;
    if (this.token) {
      localStorage.setItem('authToken', this.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  }

  async changePasswordFirstLogin(email: string, currentPassword: string, newPassword: string) {
    const response = await this.api.post('/auth/change-password', {
      email,
      currentPassword,
      newPassword
    });
    return response.data;
  }

  async logout() {
    try {
      await this.api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if server call fails
    }
    this.token = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }

  async getCurrentUser() {
    const response = await this.api.get('/auth/me');
    return response.data.user;
  }

  // Users
  async getUsers() {
    const response = await this.api.get('/users');
    return response.data;
  }

  async getUser(id: string) {
    const response = await this.api.get(`/users/${id}`);
    return response.data;
  }

  async createUser(userData: any) {
    const response = await this.api.post('/users', userData);
    return response.data;
  }

  async updateUser(id: string, userData: any) {
    const response = await this.api.put(`/users/${id}`, userData);
    return response.data;
  }

  async deleteUser(id: string) {
    const response = await this.api.delete(`/users/${id}`);
    return response.data;
  }

  async unlockUser(id: string) {
    const response = await this.api.post(`/users/${id}/unlock`);
    return response.data;
  }

  // Applications
  async getApplications(customerId?: string, modelId?: string) {
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', customerId);
    if (modelId) params.append('model_id', modelId);
    params.append('_t', Date.now().toString());
    
    console.log('🔄 API Service: Calling /applications endpoint...');
    const response = await this.api.get(`/applications?${params.toString()}`);
    console.log('📥 API Service: Raw applications response:', response.data);
    // Transform snake_case API response to camelCase for TypeScript interfaces
    const transformedApps = response.data.map((app: any) => ({
      id: app.id,
      name: app.name,
      description: app.description,
      customerId: app.customer_id,
      aprModelId: app.apr_model_id,
      costs: [], // Costs will be loaded separately if needed
      responses: [], // Responses will be loaded separately if needed
      recommendation: app.recommendation,
      overallScore: app.overall_score,
      // Application Master Data fields
      applicationIdCode: app.application_id_code,
      itHardwareInfrastructure: app.it_hardware_infrastructure,
      dateOfIntroduction: app.date_of_introduction,
      applicationOwner: app.application_owner,
      appOwnerEmail: app.app_owner_email,
      technicalOwnerCustodian: app.technical_owner_custodian,
      toEmail: app.to_email,
      businessFunctionDomain: app.business_function_domain,
      applicationType: app.application_type,
      technologyStack: app.technology_stack,
      currentOperating: app.current_operating,
      userCount: app.user_count,
      businessCriticality: app.business_criticality,
      technicalHealthStatus: app.technical_health_status,
      integrationDependencies: app.integration_dependencies,
      vendorSupplier: app.vendor_supplier,
      parentApplicationId: app.parent_application_id,
      applicationDescription: app.application_description,
      internalNumber: app.internal_number,
      l1BusinessFunctionalArea: app.l1_business_functional_area,
      l2CapabilityFunction: app.l2_capability_function,
      parentApplicationName: app.parent_application_name,
      parentApplicationCode: app.parent_application_code,
      isParent: app.is_parent,
      // Audit fields
      createdBy: app.created_by,
      createdByName: app.created_by_name,
      createdAt: app.created_at,
      updatedBy: app.updated_by,
      updatedByName: app.updated_by_name,
      updatedAt: app.updated_at
    }));
    console.log('🔄 API Service: Transformed applications:', transformedApps);
    return transformedApps;
  }

  async getApplication(id: string) {
    const response = await this.api.get(`/applications/${id}`);
    return response.data;
  }

  async createApplication(appData: any) {
    const response = await this.api.post('/applications', appData);
    return response.data;
  }

  async bulkImportApplications(applications: any[]) {
    const response = await this.api.post('/applications/bulk-import', { applications });
    return response.data;
  }

  async bulkUpdateApplications(updates: any[]) {
    const response = await this.api.post('/applications/bulk-update', { updates });
    return response.data;
  }

  async updateApplication(id: string, appData: any) {
    const response = await this.api.put(`/applications/${id}`, appData);
    return response.data;
  }

  async deleteApplication(id: string) {
    const response = await this.api.delete(`/applications/${id}`);
    return response.data;
  }

  async getApplicationCosts(id: string) {
    const response = await this.api.get(`/applications/${id}/costs`);
    return response.data;
  }

  async updateApplicationRunRate(id: string, runRateData: any) {
    const response = await this.api.put(`/applications/${id}/run-rate`, runRateData);
    return response.data;
  }

  // Questions
  async getQuestions() {
    const response = await this.api.get('/questions');
    return response.data;
  }

  async getQuestionsByDimension(dimensionId: string) {
    const response = await this.api.get(`/questions/dimension/${dimensionId}`);
    return response.data;
  }

  async getQuestion(id: string) {
    const response = await this.api.get(`/questions/${id}`);
    return response.data;
  }

  async updateAttributeWeight(attributeId: string, weight: number, customerId?: string) {
    const response = await this.api.put(`/questions/attribute/${attributeId}/weight`, { 
      weight, 
      customer_id: customerId 
    });
    return response.data;
  }

  // Assessments
  async getAssessmentResponses(applicationId: string, aprRunId?: string) {
    const params = aprRunId ? `?apr_run_id=${aprRunId}` : '';
    const response = await this.api.get(`/assessments/application/${applicationId}${params}`);
    return response.data;
  }

  async submitAssessmentResponse(data: {
    application_id: string;
    question_id: string;
    answer_option_id: string;
    apr_run_id?: string;
  }) {
    const response = await this.api.post('/assessments', data);
    return response.data;
  }

  async submitBulkAssessmentResponses(applicationId: string, responses: any[], aprRunId?: string) {
    const response = await this.api.post('/assessments/bulk', {
      application_id: applicationId,
      apr_run_id: aprRunId,
      responses
    });
    return response.data;
  }

  // Alias for backward compatibility with QuestionsAnswersModal
  async submitBulkAssessment(applicationId: string, aprRunId: string | undefined, responses: any[]) {
    return this.submitBulkAssessmentResponses(applicationId, responses, aprRunId);
  }

  async getApplicationQuestions(applicationId: string, aprRunId?: string) {
    const params = new URLSearchParams();
    params.append('application_id', applicationId);
    if (aprRunId) {
      params.append('apr_run_id', aprRunId);
    }
    
    const response = await this.api.get(`/assessments/questions?${params.toString()}`);
    return response.data;
  }

  async initializeQAAssessments(applications: any[], aprRunId?: string, aprModelId?: string) {
    const response = await this.api.post('/assessments/initialize-qa', {
      applications,
      apr_run_id: aprRunId,
      apr_model_id: aprModelId
    });
    return response.data;
  }

  async getAssessmentScores(applicationId: string, aprRunId?: string) {
    const params = aprRunId ? `?apr_run_id=${aprRunId}` : '';
    const response = await this.api.get(`/assessments/application/${applicationId}/scores${params}`);
    return response.data;
  }

  // Q&A Assessment methods
  async getQAAssessmentData(applicationId: string, aprRunId?: string) {
    const params = aprRunId ? `?apr_run_id=${aprRunId}` : '';
    const response = await this.api.get(`/assessments/qa/${applicationId}${params}`);
    return response.data;
  }

  async saveAssessmentResponse(data: {
    application_id: string;
    question_id: string;
    answer_option_id: string;
    apr_run_id?: string;
    score?: number;
    weight?: number;
  }) {
    const response = await this.api.post('/assessments/qa-answer', data);
    return response.data;
  }

  async getQAStatuses(): Promise<{ application_id: string; total_questions: number; answered_questions: number }[]> {
    const response = await this.api.get('/assessments/qa-statuses');
    return response.data;
  }

  async getScoringPortfolioData(statusFilter?: string) {
    const params = statusFilter ? `?status_filter=${statusFilter}` : '';
    const response = await this.api.get(`/assessments/scoring-portfolio${params}`);
    return response.data;
  }

  async rescoreAllApplications() {
    const response = await this.api.post('/assessments/rescore-all');
    return response.data;
  }

  async rescoreApplication(applicationId: string, aprRunId?: string) {
    const response = await this.api.post('/assessments/rescore-application', {
      application_id: applicationId,
      apr_run_id: aprRunId || null
    });
    return response.data;
  }

  async getApplicationMasterData(applicationId: string) {
    const response = await this.api.get(`/applications/${applicationId}`);
    return response.data;
  }


  // Dimensions
  async getFrameworkStats() {
    const response = await this.api.get('/dimensions/stats');
    return response.data;
  }

  async getDimensions() {
    const response = await this.api.get('/dimensions');
    return response.data;
  }

  async createDimension(dimensionData: { name: string; description?: string }) {
    const response = await this.api.post('/dimensions', dimensionData);
    return response.data;
  }

  async updateDimension(id: string, dimensionData: { name: string; description?: string }) {
    const response = await this.api.put(`/dimensions/${id}`, dimensionData);
    return response.data;
  }

  async deleteDimension(id: string) {
    const response = await this.api.delete(`/dimensions/${id}`);
    return response.data;
  }

  // Components
  async getComponents() {
    const response = await this.api.get('/components');
    return response.data;
  }

  async createComponent(componentData: { name: string; description?: string; dimensionId?: string }) {
    const response = await this.api.post('/components', componentData);
    return response.data;
  }

  async updateComponent(id: string, componentData: { name: string; description?: string; dimensionId?: string }) {
    const response = await this.api.put(`/components/${id}`, componentData);
    return response.data;
  }

  async deleteComponent(id: string) {
    const response = await this.api.delete(`/components/${id}`);
    return response.data;
  }

  // Attributes
  async getAttributes() {
    const response = await this.api.get('/attributes');
    return response.data;
  }

  async createAttribute(attributeData: { name: string; description?: string; weight: number; componentId: string }) {
    const response = await this.api.post('/attributes', attributeData);
    return response.data;
  }

  async updateAttribute(id: string, attributeData: { name: string; description?: string; weight: number; componentId?: string }) {
    const response = await this.api.put(`/attributes/${id}`, attributeData);
    return response.data;
  }

  async deleteAttribute(id: string) {
    const response = await this.api.delete(`/attributes/${id}`);
    return response.data;
  }

  async importQuestionsFromCSV(questions: any[]) {
    const response = await this.api.post('/questions/import-csv', { questions });
    return response.data;
  }

  async createQuestion(questionData: { text: string; attributeId: string; answerOptions?: any[] }) {
    const response = await this.api.post('/questions', questionData);
    return response.data;
  }

  async updateQuestion(id: string, questionData: { text: string; answerOptions?: any[] }) {
    const response = await this.api.put(`/questions/${id}`, questionData);
    return response.data;
  }

  async deleteQuestion(id: string) {
    const response = await this.api.delete(`/questions/${id}`);
    return response.data;
  }

  // Customers
  async getCustomers() {
    const response = await this.api.get('/customers');
    return response.data;
  }

  // Frameworks
  async getFrameworks() {
    const response = await this.api.get('/frameworks');
    return response.data;
  }

  async getActiveFrameworks() {
    const response = await this.api.get('/frameworks');
    // Filter to only return active frameworks
    return response.data.filter((fw: any) => fw.status === 'Active');
  }

  // Models
  async getModels() {
    console.log('🔄 API Service: Calling /models endpoint...');
    const response = await this.api.get(`/models?_t=${Date.now()}`);
    console.log('📥 API Service: Raw models response:', response.data);
    // Transform snake_case API response to camelCase for TypeScript interfaces
    const transformedModels = response.data.map((model: any) => ({
      id: model.id,
      name: model.name,
      description: model.description,
      status: model.status,
      frameworkId: model.framework_id,
      frameworkName: model.framework_name,
      dimensions: model.dimensions || [],
      applicationCount: model.application_count || 0,
      completedApplications: model.completed_applications || 0,
      runs: [] // APR runs will be loaded separately if needed
    }));
    console.log('🔄 API Service: Transformed models:', transformedModels);
    return transformedModels;
  }

  async getModel(id: string) {
    const response = await this.api.get(`/models/${id}`);
    const model = response.data;
    return {
      id: model.id,
      name: model.name,
      description: model.description,
      status: model.status,
      frameworkId: model.framework_id,
      frameworkName: model.framework_name,
      dimensions: model.dimensions || [],
      applicationCount: model.application_count || 0,
      completedApplications: model.completed_applications || 0,
      runs: []
    };
  }

  async createModel(modelData: any) {
    const payload = {
      name: modelData.name,
      description: modelData.description,
      status: modelData.status || 'Created',
      framework_id: modelData.frameworkId,
    };
    const response = await this.api.post('/models', payload);
    return response.data;
  }

  async updateModel(id: string, modelData: any) {
    const payload = {
      name: modelData.name,
      description: modelData.description,
      status: modelData.status,
      framework_id: modelData.frameworkId,
    };
    const response = await this.api.put(`/models/${id}`, payload);
    return response.data;
  }

  async deleteModel(id: string) {
    const response = await this.api.delete(`/models/${id}`);
    return response.data;
  }

  async getModelOwners(modelId: string, createAccounts: boolean = false) {
    const response = await this.api.post(`/models/${modelId}/invite-owners`, { createAccounts });
    return response.data;
  }

  async getModelApplicationStats(): Promise<{ assigned: number; unassigned: number }> {
    const response = await this.api.get('/models/application-stats');
    return response.data;
  }

  async getAssignedApplicationIds(): Promise<string[]> {
    const response = await this.api.get('/models/assigned-application-ids');
    return response.data;
  }

  async searchModelsByApplication(query: string): Promise<string[]> {
    const response = await this.api.get('/models/search-by-application', { params: { q: query } });
    return response.data;
  }

  async getModelApplications(modelId: string) {
    const response = await this.api.get(`/models/${modelId}/applications`);
    // Transform snake_case API response to camelCase for TypeScript interfaces
    return response.data.map((app: any) => ({
      id: app.id,
      name: app.name,
      description: app.description,
      customerId: app.customer_id,
      aprModelId: app.apr_model_id,
      costs: [], // Costs will be loaded separately if needed
      responses: [], // Responses will be loaded separately if needed
      recommendation: app.recommendation,
      overallScore: app.overall_score,
      // Application Master Data fields
      applicationIdCode: app.application_id_code,
      itHardwareInfrastructure: app.it_hardware_infrastructure,
      dateOfIntroduction: app.date_of_introduction,
      applicationOwner: app.application_owner,
      appOwnerEmail: app.app_owner_email,
      technicalOwnerCustodian: app.technical_owner_custodian,
      toEmail: app.to_email,
      businessFunctionDomain: app.business_function_domain,
      applicationType: app.application_type,
      technologyStack: app.technology_stack,
      currentOperating: app.current_operating,
      userCount: app.user_count,
      businessCriticality: app.business_criticality,
      technicalHealthStatus: app.technical_health_status,
      integrationDependencies: app.integration_dependencies,
      vendorSupplier: app.vendor_supplier,
      // Parent application flag
      isParent: app.is_parent || false
    }));
  }

  // APR Runs
  async getAPRRuns() {
    const response = await this.api.get('/runs');
    return response.data;
  }

  async createAPRRun(runData: any) {
    const response = await this.api.post('/runs', runData);
    return response.data;
  }

  // 4R Scoring Thresholds
  async getScoringThresholds() {
    const response = await this.api.get('/scoring-thresholds');
    // Transform snake_case API response to camelCase for TypeScript interfaces
    return response.data.map((threshold: any) => ({
      id: threshold.id,
      name: threshold.decision_name,
      minScore: threshold.min_score,
      maxScore: threshold.max_score,
      color: threshold.color,
      description: threshold.description,
      displayOrder: threshold.display_order
    }));
  }

  async updateScoringThreshold(id: string, thresholdData: any) {
    // Transform camelCase to snake_case for API
    const payload = {
      decision_name: thresholdData.name,
      min_score: thresholdData.minScore,
      max_score: thresholdData.maxScore,
      color: thresholdData.color,
      description: thresholdData.description
    };
    const response = await this.api.put(`/scoring-thresholds/${id}`, payload);
    return response.data;
  }

  async createScoringThreshold(thresholdData: any) {
    // Transform camelCase to snake_case for API
    const payload = {
      decision_name: thresholdData.name,
      min_score: thresholdData.minScore,
      max_score: thresholdData.maxScore,
      color: thresholdData.color,
      description: thresholdData.description
    };
    const response = await this.api.post('/scoring-thresholds', payload);
    // Transform response back to camelCase
    const threshold = response.data;
    return {
      id: threshold.id,
      name: threshold.decision_name,
      minScore: threshold.min_score,
      maxScore: threshold.max_score,
      color: threshold.color,
      description: threshold.description,
      displayOrder: threshold.display_order
    };
  }

  async deleteScoringThreshold(id: string) {
    const response = await this.api.delete(`/scoring-thresholds/${id}`);
    return response.data;
  }

  // APR Model Applications
  async updateModelApplications(modelId: string, applicationIds: string[]) {
    const response = await this.api.post(`/models/${modelId}/applications`, { applicationIds });
    return response.data;
  }


  // ===== CURRENT RUN RATE DATA METHODS =====
  
  // Get all current run rate data
  async getCurrentRunRateData() {
    console.log('🔄 API Service: Calling /current-run-rate endpoint...');
    const response = await this.api.get(`/current-run-rate?_t=${Date.now()}`);
    // Transform snake_case API response to camelCase for TypeScript interfaces
    return response.data.map((item: any) => ({
      id: item.id,
      applicationId: item.application_id,
      applicationName: item.application_name,
      totalAnnualCost: parseFloat(item.total_annual_cost) || 0,
      annualSoftwareLicensingFees: parseFloat(item.annual_software_licensing_fees) || 0,
      annualThirdPartyVendorSupport: parseFloat(item.annual_third_party_vendor_support) || 0,
      annualInfrastructureCost: parseFloat(item.annual_infrastructure_cost) || 0,
      indirectOperationalCosts: parseFloat(item.indirect_operational_costs) || 0,
      cloudSaasSubscriptionCostImpact: parseFloat(item.cloud_saas_subscription_cost_impact) || 0,
      // Audit fields
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      createdBy: item.created_by,
      updatedBy: item.updated_by,
      createdByName: item.created_by_name,
      updatedByName: item.updated_by_name,
      // Parent-child fields
      isParent: item.is_parent || false,
      isParentSummary: item.is_parent_summary || false,
      childCostCount: item.child_cost_count || 0,
      parentApplicationId: item.parent_application_id || null,
      parentApplicationName: item.parent_application_name || null,
      parentApplicationCode: item.parent_application_code || null,
      tcoStatus: item.tco_status || null,
    }));
  }

  // Bulk import current run rate data
  async bulkImportCurrentRunRateData(currentRunRateData: any[]) {
    const response = await this.api.post('/current-run-rate/bulk-import', {
      currentRunRateData
    });
    return response.data;
  }

  // Delete all current run rate data
  async deleteAllCurrentRunRateData() {
    const response = await this.api.delete('/current-run-rate/all');
    return response.data;
  }

  // Create individual current run rate record
  async createCurrentRunRateData(currentRunRateData: any) {
    const response = await this.api.post('/current-run-rate', currentRunRateData);
    return response.data;
  }

  // Get individual current run rate record
  async getCurrentRunRateRecord(id: string) {
    const response = await this.api.get(`/current-run-rate/${id}`);
    return response.data;
  }

  // Update individual current run rate record
  async updateCurrentRunRateData(id: string, currentRunRateData: any) {
    const response = await this.api.put(`/current-run-rate/${id}`, currentRunRateData);
    return response.data;
  }

  // Delete specific current run rate record
  async deleteCurrentRunRateData(applicationId: string) {
    const response = await this.api.delete(`/current-run-rate/${applicationId}`);
    return response.data;
  }

  // Bulk update current run rate data
  async bulkUpdateCurrentRunRateData(currentRunRateData: any[]) {
    const response = await this.api.put('/current-run-rate/bulk-update', { currentRunRateData });
    return response.data;
  }

  // Get available applications for current run rate
  async getAvailableApplicationsForCosts() {
    const response = await this.api.get('/current-run-rate/available-applications');
    return response.data;
  }

  // Health check
  async checkHealth() {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Answer Options
  async getAnswerOptions() {
    const response = await this.api.get('/answer-options');
    return response.data;
  }

  // Database seeding (dev only)
  async seedDatabase() {
    const response = await this.api.post('/seed');
    return response.data;
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;

// Re-export config helpers for components that need direct access
export { getApiBaseUrl, buildApiUrl } from './apiConfig';
export { getBackendBaseUrl, getAssetUrl } from './apiConfig';

/**
 * Authenticated fetch utility for components that need direct fetch access.
 * Use this instead of raw fetch() to ensure authentication headers are included.
 *
 * URL Resolution:
 * - Absolute URLs (http://...) are used as-is
 * - Relative URLs starting with /api are resolved using environment-aware config
 * - Other relative URLs are resolved using buildApiUrl
 *
 * @example
 * import { authFetch } from '../services/api';
 * // These all work correctly in both development and production:
 * const response = await authFetch('/api/endpoint');
 * const response = await authFetch('/endpoint');  // Will prepend API base
 * const data = await response.json();
 */
export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('authToken');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };

  // Resolve URL using centralized config
  // If URL is absolute (starts with http), use as-is for backward compatibility
  // Otherwise, use buildApiUrl for environment-aware resolution
  const resolvedUrl = (url.startsWith('http://') || url.startsWith('https://'))
    ? url
    : buildApiUrl(url);

  return fetch(resolvedUrl, {
    ...options,
    headers
  });
};